import { db } from '../config/firebase';
import { collection, query, where, getDocs, addDoc, Timestamp, orderBy, updateDoc, doc } from 'firebase/firestore';
import { FieldStaffPayment } from '../types/billing.types';
import { User } from '../types/user.types';
import { Lead } from '../types/lead.types';
import { endOfWeek } from 'date-fns';
import { projectService } from './project.service';

export const fieldPaymentService = {
  async getProjectPayments(projectId: string): Promise<FieldStaffPayment[]> {
    try {
      const q = query(
        collection(db, 'fieldPayments'),
        where('projectId', '==', projectId),
        orderBy('weekStart', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FieldStaffPayment));
    } catch (error) {
      console.error('Error getting project payments:', error);
      return [];
    }
  },

  async processWeeklyPayments(projectId: string, weekStart: Date): Promise<void> {
    try {
      // Get project
      const project = await projectService.getProject(projectId);
      if (!project) return;

      // Get field users for project
      const usersQuery = query(
        collection(db, 'users'),
        where('projects', 'array-contains', projectId),
        where('role', '==', 'field')
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      const fieldUsers = usersSnapshot.docs.map(doc => ({
        ...doc.data(),
        uid: doc.id
      } as User));  // Cast to User now that uid is added
      
      // Process payment for each field user
      for (const user of fieldUsers) {
        // Get user's leads for the week
        const leadsQuery = query(
          collection(db, 'leads'),
          where('projectId', '==', projectId),
          where('capturedBy', '==', user.uid),
          where('createdAt', '>=', Timestamp.fromDate(weekStart)),
          where('createdAt', '<=', Timestamp.fromDate(endOfWeek(weekStart, { weekStartsOn: 0 })))
        );
        
        const snapshot = await getDocs(leadsQuery);
        const leads = snapshot.docs.map(doc => doc.data() as Lead);
        
        // Calculate payment
        let totalLeads = leads.length;
        let validLeads = 0;
        let invalidLeads = 0;
        let pendingLeads = 0;
        let totalStudents = 0;
        let validStudents = 0;
        let enrolledStudents = 0;

        leads.forEach(lead => {
          const children = lead.children || [];
          totalStudents += children.length;

          // Per-STUDENT valid/enrolled is the billing/payroll source of truth.
          // Prefer each child's own determination/status; fall back to the
          // lead-level value when a child hasn't been set individually.
          children.forEach(child => {
            const det = child.determination || lead.determination || 'pending';
            if (det === 'valid') validStudents++;
            const st = child.status || lead.status;
            if (st === 'enrolled') enrolledStudents++;
          });

          // Lead-level tallies (for display only).
          const determination = lead.determination || 'pending';
          if (determination === 'valid') validLeads++;
          else if (determination === 'invalid') invalidLeads++;
          else pendingLeads++;
        });

        const conversionRate = validStudents > 0 ? (enrolledStudents / validStudents) * 100 : 0;
        const payRate = user.payRate || project.settings.billingInfo.payRate || 25;  // Use user payRate if set, fallback to project
        // Payroll is per valid STUDENT (not per lead) — matches the documented
        // source of truth: 50% paid weekly, balance held until the contractor
        // hits 20% conversion.
        const totalEarned = validStudents * payRate;
        const weeklyPaymentDue = totalEarned * 0.5;
        const holdbackAmount = totalEarned * 0.5;

        // Check thresholds
        const meetsIndividualThreshold = conversionRate >= 20;
        const projectStats = await projectService.getProjectStats(projectId);
        const projectGoalMet = projectStats.enrollments >= project.goals.enrollmentTarget;

        const payment: Omit<FieldStaffPayment, 'id'> = {
          userId: user.uid,
          userName: user.name || '',
          projectId,
          projectName: project.name,
          weekStart,
          weekEnd: endOfWeek(weekStart, { weekStartsOn: 0 }),
          totalLeads,
          validLeads,
          invalidLeads,
          pendingLeads,
          totalStudents,
          validStudents,
          enrolledStudents,
          conversionRate,
          meetsThreshold: meetsIndividualThreshold,
          payRate,
          totalEarned,
          weeklyPaymentDue,
          weeklyPaymentPaid: false,
          holdbackAmount,
          holdbackReleased: false,
          totalPaid: 0,
          totalHoldback: holdbackAmount,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };
        
        await addDoc(collection(db, 'fieldPayments'), payment);
      }
    } catch (error) {
      console.error('Error processing weekly payments:', error);
      throw error;
    }
  },

  async releaseHoldback(paymentId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'fieldPayments', paymentId), {
        holdbackReleased: true,
        holdbackReleaseDate: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error releasing holdback:', error);
      throw error;
    }
  },

  async markWeeklyPaymentPaid(paymentId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'fieldPayments', paymentId), {
        weeklyPaymentPaid: true,
        weeklyPaymentDate: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      throw error;
    }
  }
};